import { initHostFederation, loadChatMfeInBackground } from './federation-init';
import { environment } from './environments/environment';

// Shims de globales de Node.js requeridos por aws-amplify y subscriptions-transport-ws
(window as any).global = window;
(window as any).process = { env: { DEBUG: undefined } };

// Remote modules loaded via Native Federation run in this host's global scope but
// are compiled separately, so esbuild never inlines ngDevMode for them.
// This guard must run before initFederation() loads any remote code.
// Uses environment.production so Angular DevTools correctly detects dev vs prod mode.
(globalThis as any).ngDevMode ??= !environment.production;

/**
 * Presupuesto de cortesía (ms) para que la app quede "stable" antes de
 * arrancar la Fase 2 de todos modos. Medido con Playwright (5 corridas contra
 * el home real): `isStable` tarda en promedio ~2.6-2.9s en emitir `true` (hasta
 * ~3.8s en una corrida más lenta) — bastante más que los 3s que tenía este
 * fallback antes, es decir, casi coincidían. Si el fallback dispara ANTES que
 * `isStable`, se reintroduce parte de la contención que este mecanismo existe
 * para evitar. Subido a 5000ms para que sea un verdadero backstop de peor
 * caso, no algo que se cruce con el tiempo normal de estabilización.
 */
const CHAT_MFE_STABLE_FALLBACK_MS = 5000;

/**
 * Timeout de respaldo para requestIdleCallback. Se usa DESPUÉS de que
 * `isStable` ya confirmó true — no como señal única (eso se probó y se
 * descartó por disparar demasiado pronto, ver docs/chat-mfe-decoupled-
 * bootstrap-requirements.md). Aquí solo afina el instante exacto de arranque:
 * si el hilo principal sigue ocupado justo cuando `isStable` cambia (puede
 * coincidir con el propio ciclo de render), esperamos un hueco real de
 * inactividad antes de disparar la Fase 2. Safari no implementa
 * requestIdleCallback, de ahí el fallback a setTimeout.
 */
const CHAT_MFE_IDLE_FALLBACK_MS = 500;

function waitForIdle(timeoutMs: number): Promise<void> {
  return new Promise<void>(resolve => {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => resolve(), { timeout: timeoutMs });
    } else {
      setTimeout(resolve, timeoutMs);
    }
  });
}

async function startApp() {
  // Fase 1 — host-only, same-origin: rápida, y Angular no puede arrancar sin
  // que termine (import map del host). Ver federation-init.ts.
  await initHostFederation();

  const { bootstrap } = await import('./bootstrap');
  const moduleRef = await bootstrap();

  // Fase 2 — registro del remoto chat-mfe + sus 3 módulos, incluida la
  // construcción de RealtimeService/RealtimeLifecycleService (provideChat()
  // las fuerza de forma eager). Medido con Playwright: arrancar esto en
  // paralelo con el bootstrap compite por el hilo principal con el primer
  // render + las llamadas iniciales del home, y retrasa el contenido real de
  // la página varios cientos de ms — pese a que no hay ningún await entre
  // ambas. Por eso se pospone hasta que Angular esté "stable" (o venza el
  // presupuesto de cortesía), dejando el render crítico sin contención.
  if (moduleRef) {
    void startChatMfeWhenStable(moduleRef);
  } else {
    loadChatMfeInBackground();
  }
}

async function startChatMfeWhenStable(moduleRef: import('@angular/core').NgModuleRef<unknown>): Promise<void> {
  const { ApplicationRef } = await import('@angular/core');
  const appRef = moduleRef.injector.get(ApplicationRef);

  await new Promise<void>(resolve => {
    const fallback = setTimeout(resolve, CHAT_MFE_STABLE_FALLBACK_MS);
    const sub = appRef.isStable.subscribe(stable => {
      if (!stable) return;
      clearTimeout(fallback);
      sub.unsubscribe();
      resolve();
    });
  });

  await waitForIdle(CHAT_MFE_IDLE_FALLBACK_MS);

  loadChatMfeInBackground();
}

startApp().catch(err => console.error(err));
